import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useAuthStore } from '@/stores/auth.store';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Landing } from '@/pages/Landing';
import { Login } from '@/pages/Login';
import { Register } from '@/pages/Register';
import { Dashboard } from '@/pages/Dashboard';
import { Instances } from '@/pages/Instances';
import { SetupWizard } from '@/pages/SetupWizard';
import { Settings } from '@/pages/Settings';
import { Billing } from '@/pages/Billing';
import { Notifications } from '@/pages/Notifications';
import { Admin } from '@/pages/Admin';
import { InstanceDetailClean as InstanceDetail } from '@/pages/InstanceDetailClean';
import { DeployProgress } from '@/pages/DeployProgress';
import { NotFound } from '@/pages/NotFound';
import { ForgotPassword } from '@/pages/ForgotPassword';
import { VerifyOtp } from '@/pages/VerifyOtp';
import { ResetPassword } from '@/pages/ResetPassword';
import { SkillBrowser } from '@/pages/SkillBrowser';
import { Marketplace } from '@/pages/Marketplace';
import { Teams } from '@/pages/Teams';
import { Affiliates } from '@/pages/Affiliates';
import { Webhooks } from '@/pages/Webhooks';
import { ApiKeys } from '@/pages/ApiKeys';
import { AdminAnalytics } from '@/pages/AdminAnalytics';
import { BlogList } from '@/pages/blog/BlogList';
import { BlogPost } from '@/pages/blog/BlogPost';
import { CreateBlog } from '@/pages/blog/CreateBlog';
import { UpdateBlog } from '@/pages/blog/UpdateBlog';
import { MyBlogs } from '@/pages/blog/MyBlogs';
import { Docs } from '@/pages/Docs';
import { OAuthCallback } from '@/pages/OAuthCallback';
import { PrivacyPage } from '@/pages/static/PrivacyPage';
import { TermsPage } from '@/pages/static/TermsPage';
import { CookiesPage } from '@/pages/static/CookiesPage';
import { DataDeletionPage } from '@/pages/static/DataDeletionPage';
import { ReactNode } from 'react';

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      // Wait one frame so the new route's sections are mounted before we
      // try to scroll to them.
      const id = hash.slice(1);
      requestAnimationFrame(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        window.scrollTo(0, 0);
      });
      return;
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function AdminRoute({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.role !== 'ADMIN') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-otp" element={<VerifyOtp />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/create" element={<CreateBlog />} />
        <Route path="/blog/edit/:id" element={<UpdateBlog />} />
        <Route path="/blog/my-posts" element={<MyBlogs />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        <Route path="/auth/callback" element={<OAuthCallback />} />

        {/* Dashboard routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="instances" element={<Instances />} />
          <Route path="instances/:id" element={<InstanceDetail />} />
          <Route path="new" element={<SetupWizard />} />
          <Route path="deploy/:id" element={<DeployProgress />} />
          <Route path="skills" element={<SkillBrowser />} />
          <Route path="marketplace" element={<Marketplace />} />
          <Route path="teams" element={<Teams />} />
          <Route path="affiliates" element={<Affiliates />} />
          <Route path="billing" element={<Billing />} />
          <Route path="webhooks" element={<Webhooks />} />
          <Route path="api-keys" element={<ApiKeys />} />
          <Route path="settings" element={<Settings />} />
          <Route path="notifications" element={<Notifications />} />
        </Route>

        {/* Admin routes */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <DashboardLayout />
            </AdminRoute>
          }
        >
          <Route index element={<Admin />} />
          <Route path="instances" element={<Admin />} />
          <Route path="blog" element={<Admin />} />
          <Route path="analytics" element={<AdminAnalytics />} />
        </Route>

        {/* Catch-all */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
